import React from 'react';
import { Button } from '@shadcn/components/ui/button';
import { Plus, FolderPlus, Trash2, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MAX_CONDITION_NESTING_DEPTH } from '../../../constants';
import ConditionRowEditor from './ConditionRowEditor';

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-1">
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="mt-1.5 p-0.5 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0"
      >
        <GripVertical size={12} />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

const LogicSeparator = ({ logic, onClick }) => (
  <div className="flex items-center gap-2 py-0.5">
    <div className="flex-1 border-t border-gray-200" />
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-0.5 text-[10px] font-semibold rounded-full border transition-colors bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100"
    >
      {logic}
    </button>
    <div className="flex-1 border-t border-gray-200" />
  </div>
);

const ConditionGroupNode = ({ group, onChange, onRemove, depth = 0, fields = [] }) => {
  const isRoot = depth === 0;
  const canNest = depth < MAX_CONDITION_NESTING_DEPTH;
  const conditions = group.conditions || [];
  const groups = group.groups || [];
  const totalItems = conditions.length + groups.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const toggleLogic = () => {
    onChange({ ...group, logic: group.logic === 'AND' ? 'OR' : 'AND' });
  };

  // --- Condition mutations ---

  const updateCondition = (index, updated) => {
    const next = [...conditions];
    next[index] = updated;
    onChange({ ...group, conditions: next });
  };

  const removeCondition = (index) => {
    onChange({ ...group, conditions: conditions.filter((_, i) => i !== index) });
  };

  const addCondition = () => {
    onChange({
      ...group,
      conditions: [...conditions, { field: '', operator: '', value: '' }],
    });
  };

  // --- Sub-group mutations ---

  const updateSubGroup = (index, updated) => {
    const next = [...groups];
    next[index] = updated;
    onChange({ ...group, groups: next });
  };

  const removeSubGroup = (index) => {
    onChange({ ...group, groups: groups.filter((_, i) => i !== index) });
  };

  const addSubGroup = () => {
    onChange({
      ...group,
      groups: [
        ...groups,
        { logic: 'OR', conditions: [{ field: '', operator: '', value: '' }], groups: [] },
      ],
    });
  };

  // Build a flat render list of conditions then groups, with separators between them
  const allItems = [
    ...conditions.map((c, i) => ({ kind: 'condition', index: i, data: c, id: `condition-${i}` })),
    ...groups.map((g, i) => ({ kind: 'group', index: i, data: g, id: `group-${i}` })),
  ];

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeItem = allItems.find(i => i.id === active.id);
    const overItem = allItems.find(i => i.id === over.id);
    if (!activeItem || !overItem || activeItem.kind !== overItem.kind) return;

    if (activeItem.kind === 'condition') {
      onChange({ ...group, conditions: arrayMove(conditions, activeItem.index, overItem.index) });
    } else {
      onChange({ ...group, groups: arrayMove(groups, activeItem.index, overItem.index) });
    }
  };

  return (
    <div className={`${!isRoot ? 'ml-3 border-l-2 border-amber-300 pl-3 py-1' : ''}`}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={allItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-0">
            {allItems.map((item, pos) => (
              <React.Fragment key={item.id}>
                {/* AND/OR separator between items */}
                {pos > 0 && (
                  <LogicSeparator logic={group.logic} onClick={toggleLogic} />
                )}

                <SortableItem id={item.id}>
                  {item.kind === 'condition' ? (
                    <ConditionRowEditor
                      condition={item.data}
                      onChange={(updated) => updateCondition(item.index, updated)}
                      onRemove={totalItems > 1 ? () => removeCondition(item.index) : null}
                      fields={fields}
                    />
                  ) : (
                    <ConditionGroupNode
                      group={item.data}
                      onChange={(updated) => updateSubGroup(item.index, updated)}
                      onRemove={() => removeSubGroup(item.index)}
                      depth={depth + 1}
                      fields={fields}
                    />
                  )}
                </SortableItem>
              </React.Fragment>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Footer: add / remove buttons */}
      <div className="flex items-center gap-2 mt-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-gray-500"
          onClick={addCondition}
        >
          <Plus size={12} className="mr-1" />
          Condition
        </Button>
        {canNest && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-gray-500"
            onClick={addSubGroup}
          >
            <FolderPlus size={12} className="mr-1" />
            Group
          </Button>
        )}
        {!isRoot && onRemove && (
          <>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-red-400 hover:text-red-600"
              onClick={onRemove}
            >
              <Trash2 size={12} className="mr-1" />
              Remove
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ConditionGroupNode;